import React from "react";
import { get } from "lodash";
import { PanelCard } from "@sonder/ui/source/styled/fragments/PanelCard";
import { PanelCardProperty } from "@sonder/ui/source/styled/fragments/PanelCardProperty";
import { PanelCardSectionHeader } from "@sonder/ui/source/styled/fragments/PanelCardSectionHeader";

// Types
interface Property {
  label: string;
  value: React.ReactNode;
}
interface PropertyOption<Data> {
  label: string;
  path: string;
  transform(value: any, data: Data): React.ReactNode;
  shouldShow?: (value: any, data: Data) => boolean | null;
}

// Exports
interface PanelPropertiesCardProps<Data> {
  data: Data;
  emptyNode?: React.ReactNode;
  heading?: string;
  options: PropertyOption<Data>[];
}
export function PanelPropertiesCard<Data>({
  data,
  emptyNode,
  heading,
  options,
}: PanelPropertiesCardProps<Data>) {
  const properties = options
    .filter((option: PropertyOption<Data>) =>
      option && option.shouldShow
        ? option.shouldShow(get(data, option.path, null), data)
        : true,
    )
    .map((option: PropertyOption<Data>) => {
      const { label, path, transform } = option;
      const rawValue: string | null = get(data, path, null);
      return rawValue ? { label, value: transform(rawValue, data) } : null;
    })
    .reduce((acc, item) => (item ? acc.concat(item) : acc), [] as Property[]);

  if (properties.length === 0) {
    return emptyNode ? (
      <PanelCard>
        <PanelCardProperty value={emptyNode} />
      </PanelCard>
    ) : null;
  }

  return (
    <PanelCard>
      {heading && <PanelCardSectionHeader>{heading}</PanelCardSectionHeader>}
      {properties.map(({ label, value }) => (
        <PanelCardProperty key={label} label={label} value={value} />
      ))}
    </PanelCard>
  );
}
